/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        ImpersonateUserModal Component (Task 11.4.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  LogIn,
  X,
  AlertTriangle,
  ExternalLink,
  ShieldCheck,
  Clock,
  Loader2,
  Layers,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { adminClient } from '../lib/api-client';
import type {
  AdminUserListItem,
  AdminImpersonationResponse,
} from '@sofia/shared-types';

export interface ImpersonateUserModalProps {
  user: AdminUserListItem | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (targetEmail: string) => void;
}

export const ImpersonateUserModal: React.FC<ImpersonateUserModalProps> = ({
  user,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useI18n();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const mutation = useMutation<
    { success: boolean; data: AdminImpersonationResponse },
    Error,
    string
  >({
    mutationFn: async (userId: string) => {
      return adminClient.post(`/api/v1/admin/users/${userId}/impersonate`, {});
    },
    onSuccess: (res) => {
      if (res.data?.redirectUrl) {
        // Open user workspace session in a new browser tab
        window.open(res.data.redirectUrl, '_blank', 'noopener,noreferrer');
      }
      if (user) {
        onSuccess(user.email);
      }
      onClose();
    },
    onError: (err) => {
      setErrorMsg(err.message);
    },
  });

  if (!isOpen || !user) return null;

  const handleConfirm = () => {
    setErrorMsg(null);
    mutation.mutate(user.id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 backdrop-blur-sm p-4 sm:p-6 animate-in fade-in duration-150">
      <div className="w-full max-w-md rounded-3xl bg-surface border border-surface-border shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border bg-surface-elevated shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500">
              <LogIn className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-heading font-bold text-foreground">
                {t('admin.impersonation.modalTitle')}
              </h3>
              <p className="text-[11px] text-muted-fg font-mono">#{user.id.slice(0, 8)}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="p-1.5 rounded-xl text-muted-fg hover:text-foreground hover:bg-surface transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 space-y-4">
          {/* Target User Info Card */}
          <div className="p-4 rounded-2xl bg-surface-elevated border border-surface-border space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-foreground">{user.fullName}</span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary border border-primary/20">
                {user.role}
              </span>
            </div>
            <p className="text-xs text-muted-fg">{user.email}</p>
            <div className="flex items-center gap-1.5 pt-1 text-[11px] text-muted-fg">
              <Layers className="w-3.5 h-3.5 text-primary shrink-0" />
              <span>Workspace:</span>
              <strong className="text-foreground">{user.workspaceName}</strong>
            </div>
          </div>

          {/* Description */}
          <p className="text-xs text-muted-fg leading-relaxed">
            {t('admin.impersonation.modalDesc').replace('{email}', user.email)}
          </p>

          {/* Security & Compliance Highlights */}
          <div className="space-y-2 p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-900 dark:text-amber-200">
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <span className="text-[11px] leading-snug">
                {t('admin.impersonation.securityNotice')}
              </span>
            </div>
            <div className="flex items-center gap-2 pt-1 border-t border-amber-500/20 text-[11px] font-medium text-amber-800 dark:text-amber-300">
              <ShieldCheck className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              <span>Signed with HMAC-SHA256 & logged into Immutable Audit Trail</span>
            </div>
          </div>

          {/* Error Message */}
          {errorMsg && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-xs text-rose-500">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-surface-border">
            <button
              type="button"
              onClick={onClose}
              disabled={mutation.isPending}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-muted-fg hover:text-foreground hover:bg-surface-elevated border border-surface-border transition-colors disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>

            <button
              type="button"
              onClick={handleConfirm}
              disabled={mutation.isPending}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-black transition-all shadow-md disabled:opacity-50"
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('admin.impersonation.launching')}</span>
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4" />
                  <span>{t('admin.impersonation.confirmBtn')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
